import React, { ReactNode } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { UserAvatar } from '../../../common/Avatar'
import { theme } from '../../../../theme/theme'
import {
  isUserAdminInSociety,
  translateRole,
} from '../../../../helpers/society'
import { IconChoices } from '../../../common/Icon'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { Dropdown } from '../../../common/Dropdown'
import { useModal } from '../../../../hooks/useModal'
import { CreateUpdateBoardMemberModal } from '../CreateUpdateBoardMemberModal'
import { Button, ButtonVariant } from '../../../common/Button'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { userQueries } from '../../../../api/users'
import { getUserFullName } from '../../../../helpers/user'
import { IBoardMember } from '../../../../interfaces/models/societies.interfaces'
import { deleteUserRole } from '../../../../api/societies'
import { NSocieties } from '../../../../interfaces/services/societies.interfaces'
import { useAuth } from '../../../../hooks/useAuth'
import { i18nSocietyType } from '../../../../i18n'

interface BoardListItemProps {
  boardMember: IBoardMember
}

export const BoardListItem = ({
  boardMember,
}: BoardListItemProps): ReactNode => {
  const navigate = useNavigate()
  const { society } = useCurrentSociety()
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const {
    show: showEditModal,
    open: openEditModal,
    close: closeEditModal,
  } = useModal()
  const {
    show: showDeleteConfirmationModal,
    close: closeDeleteConfirmationModal,
    open: openDeleteConfirmationModal,
  } = useModal()

  const { userId } = useAuth()
  const { data: user } = useQuery(userQueries.getUser(boardMember.userId))

  const isAdmin = isUserAdminInSociety(society, userId as string)

  const { mutate: mutateDeleteUserRole } = useMutation({
    mutationFn: async (body: NSocieties.NRemoveUsersRoles.IRequestBody[0]) => {
      const status = await deleteUserRole([body])
      if (!status) throw new Error()
      return status
    },
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('boardListView.flashMessage.deleteBoardMemberSuccess', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('boardListView.flashMessage.deleteBoardMemberFailure', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })
      )
    },
  })

  const deleteBoardMember = async (): Promise<void> => {
    if (!society) return
    mutateDeleteUserRole({
      societyId: society?._id,
      userId: boardMember.userId,
      boardRole: true,
      nominatingCommitteeRole: true,
    })
  }

  const dropdownOptions = [
    {
      value: 'edit',
      label: translate('boardListItem.dropdown.editRole'),
      onClick: () => openEditModal(),
    },
    {
      value: 'remove',
      label: translate('common.actions.remove'),
      onClick: openDeleteConfirmationModal,
      destructive: true,
    },
  ]

  const getUserRoute = (): string => {
    return reverseUrl('user:society-detail', {
      id: boardMember.userId,
      societyId: society?._id,
    })
  }

  const userOnClick = (): void => {
    return navigate(getUserRoute())
  }

  const fullName = getUserFullName(user)

  return (
    <>
      <div className="flex flex-row items-center rounded-sm border-b py-5 px-4">
        <div className="flex flex-10 flex-row items-center">
          <UserAvatar user={user} onClick={userOnClick} />
          <div className="ml-4 flex flex-col justify-start">
            <p style={theme.textVariants.baseBold}>
              {translateRole(boardMember.role)}
            </p>
            <Link
              className="hover:underline"
              to={getUserRoute()}
              style={theme.textVariants.baseBold}
            >
              <p
                className="text-gray-600"
                style={theme.textVariants.base}
                translate="no"
              >
                {fullName}
              </p>
            </Link>
          </div>
        </div>
        {isAdmin && (
          <Dropdown options={dropdownOptions}>
            <Button
              variant={ButtonVariant.TEXT}
              size="xs"
              icon={IconChoices.MORE}
              className="h-10 w-10 bg-transparent hover:bg-neutral-90"
              disableTabIndex
            />
          </Dropdown>
        )}
      </div>
      <ConfirmationModal
        title={translate('boardListView.alert.deleteBoardMemberTitle', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        description={translate('boardListView.alert.deleteBoardMemberText', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        show={showDeleteConfirmationModal}
        close={closeDeleteConfirmationModal}
        onConfirm={deleteBoardMember}
      />
      {society && (
        <CreateUpdateBoardMemberModal
          society={society}
          show={showEditModal}
          close={closeEditModal}
          userId={boardMember.userId}
        />
      )}
    </>
  )
}
